<script>
export default {
  title: 'Project 2b: Heat Transfer',
  starterId: null
}
</script>

<template>
  <section>

    <article>
      <h2>Reading</h2>
      <ul>
        <li><cite>Mastering MATLAB (1st Edition)</cite> Chapter 7</li>
        <li><cite>Mastering MATLAB (1st Edition)</cite> Chapter 10</li>
        <li><cite>Mastering MATLAB (1st Edition)</cite> Sections 11.1 - 11.4</li>
        <li><cite>Mastering MATLAB (1st Edition)</cite> Sections 12.1 - 12.3, 12.6, 12.9 - 12.11</li>
        <li><cite>Mastering MATLAB (1st Edition)</cite> Chapter 13</li>
      </ul>
    </article>

    <article>
      <h2>Background</h2>
      <p>In this problem, we work with a very simplified model of heat transfer.
        We will consider a square plate at the edges of which the temperature
        distribution is known, and determine both by computer simulation and by
        direct computation the temperature equilibrium of the object.</p>

      <figure>
        <img src="./images/heat-transfer-plate.jpg" alt="Heat Transfer Plate" class="img-responsive center-block">
        <figcaption>Type of plate your program will model</figcaption>
      </figure>
    </article>

    <article>
      <h2>Problem</h2>

      <p>This is a two-part problem. The idea is to solve this problem in two
        ways, in order to be able to check your answer. The two parts may be done
        in separate programs, or in a single program; the latter is probably
        better, since you can make more clear that you are computing the answer
        with one step and checking your answer with the other. Obviously the two
        answers should agree (within the limits of accuracy). If they don’t, there
        is a bug in your program.</p>

      <p>In both parts, we conceptually represent the interior of the square
        plate by a 3-by-3 double array, each cell of which contains the temperature
        in degrees Kelvin of the corresponding interior portion of the plate. Your
        programs will start by initializing a matrix that represents the
        temperature at points on the exterior (sixteen values) and the interior
        (nine values) of the plate. Do this either by executing an assignment
        statement or by reading its values from a file. Note that the temperatures
        at the exterior are constant, and the temperatures at the interior are
        (arbitrary) initial values.</p>
    </article>

    <article>
      <h2>Part 1</h2>

      <p>The program for Part 1 will then enter a loop that simulates heat loss
        or gain by the plate over a period of time, according to the following
        rule: the temperature in cell [<var>r</var>, <var>c</var>] of the 3-by-3
        array at state <var>t</var> is the average of the temperatures in the four
        cells [<var>r</var>–1, <var>c</var>], [<var>r</var>, <var>c</var>–1],
        [<var>r</var>, <var>c</var>+1], and [<var>r</var>+1, <var>c</var>] at state
        <var>t</var>–1 (cells [0, <var>c</var>], [<var>r</var>, 0], [4,
        <var>c</var>], and [<var>r</var>, 4] represent points on the exterior of
        the plate).</p>

      <p>This loop should run until the maximum changes in temperature in all
        cells of the interior of the plate in a particular unit of time are less
        than 0.05 degree (you may wish to make this difference smaller to improve
        the accuracy of the simulation). It should then print the array of
        (equilibrium) temperature values, along with the number of time units the
        process took to reach this point.</p>

      <p>You will need a copy of the plate to implement the process described
        above correctly, since if you use only one copy you will be computing
        values at state <var>t</var> from other values in state <var>t</var> rather
        than values in state <var>t</var>–1.</p>
    </article>

    <article>
      <h2>Part 2</h2>

      <p>The program for Part 2 will compute the equilibrium temperatures by
        solving the system of equations that results from the following rule: the
        temperature in cell [<var>r</var>, <var>c</var>] of the plate must be the
        average of the temperatures in the four adjacent cells [<var>r</var>–1,
        <var>c</var>], [<var>r</var>, <var>c</var>–1], [<var>r</var>,
        <var>c</var>+1], and [<var>r</var>+1, <var>c</var>].</p>

      <p>There will be nine such equations, one for each cell in the array. Your
        program will create a 9×9 double matrix to contain the coefficients
        of the equations, along with a 9-element double array to contain their
        right-hand-sides. It will then return the solution of the system of
        equations.</p>
    </article>

    <article>
      <h3>Example</h3>

      <p>The matrix below represents a plate for which the temperatures on the
        exterior points are known and the temperatures on the interior are not.
        (Note that the numbers given here are arbitrary! Your program should be
        able to handle any set of numbers for the exterior.)</p>

      <table style="border-color: #000; border-spacing: 10px; border-style: solid; border-width: 0 1px; margin: 0 auto; text-align: center">
        <tbody><tr><td> 0.0</td><td> 4.0           </td><td> 8.0           </td><td>12.0           </td><td>16.0</td></tr>
        <tr><td>12.0</td><td> x<sub>11</sub></td><td> x<sub>12</sub></td><td> x<sub>13</sub></td><td>20.0</td></tr>
        <tr><td>20.0</td><td> x<sub>21</sub></td><td> x<sub>22</sub></td><td> x<sub>23</sub></td><td>24.0</td></tr>
        <tr><td>20.0</td><td> x<sub>31</sub></td><td> x<sub>32</sub></td><td> x<sub>33</sub></td><td>28.0</td></tr>
        <tr><td>20.0</td><td>24.0           </td><td>28.0           </td><td>28.0           </td><td>32.0</td></tr>
        </tbody></table>

      <p>Your program for Part 1 will read the values both for the exterior of
        the plate and for the interior (the
        <var>x</var><sub><var>r</var><var>c</var></sub> above). The temperatures
        you supply for the <var>x</var><sub><var>r</var><var>c</var></sub> will
        initialize the simulation; they should be such that they will convince a
        tutor that your simulation works. Note that the exterior temperatures don’t
        change during the simulation.</p>

      <p>In Part 2, you solve the following system of equations:</p>
      <table style="margin: 0 auto">
        <tbody><tr><td><var>x</var><sub>11</sub> = (12.0 + 4.0  + <var>x</var><sub>12</sub>  +  <var>x</var><sub>21</sub>) / 4</td></tr>
        <tr><td><var>x</var><sub>21</sub> = (20.0 + <var>x</var><sub>11</sub>  + <var>x</var><sub>22</sub>  +  <var>x</var><sub>31</sub>) / 4</td></tr>
        <tr><td><var>x</var><sub>31</sub> = (20.0 + <var>x</var><sub>21</sub>  + <var>x</var><sub>32</sub>  + 24.0) / 4</td></tr>
        <tr><td><var>x</var><sub>12</sub> = (<var>x</var><sub>11</sub> + 8.0  + <var>x</var><sub>13</sub>  +  <var>x</var><sub>22</sub>) / 4</td></tr>
        <tr><td><var>x</var><sub>22</sub> = (<var>x</var><sub>21</sub> + <var>x</var><sub>12</sub>  + <var>x</var><sub>23</sub>  +  <var>x</var><sub>32</sub>) / 4</td></tr>
        <tr><td><var>x</var><sub>32</sub> = (<var>x</var><sub>31</sub> + <var>x</var><sub>22</sub>  + <var>x</var><sub>33</sub>  + 28.0) / 4</td></tr>
        <tr><td><var>x</var><sub>12</sub> = (<var>x</var><sub>12</sub> + 12.0 + 20.0 +  <var>x</var><sub>23</sub>) / 4</td></tr>
        <tr><td><var>x</var><sub>23</sub> = (<var>x</var><sub>22</sub> + <var>x</var><sub>13</sub>  + 24.0 +  <var>x</var><sub>33</sub>) / 4</td></tr>
        <tr><td><var>x</var><sub>33</sub> = (<var>x</var><sub>32</sub> + <var>x</var><sub>23</sub>  + 28.0 + 28.0) / 4</td></tr>
        </tbody></table>

      <p>The <var>x</var><sub><var>r</var><var>c</var></sub> are not initialized
        for this part; they are the solutions to the system of equations. The first
        step in this process is to transform the equations from the form above to
        the form <var>A</var><var>x</var> = <var>b</var>. It helps to rewrite the
        above equations into a form more in keeping with the eventual matrices
        needed. We will make two changes: first, we will include all the variables
        (multiplying by zero when needed), and move the constant term to the right
        side of the expression. The result is</p>

      <table style="margin: 0 auto">
        <tbody><tr><td>1.0*<var>x</var><sub>11</sub>  </td><td>+    –<var>x</var><sub>21</sub>/4</td><td>+   0*<var>x</var><sub>31</sub>  </td><td>+    –<var>x</var><sub>12</sub>/4</td><td>+   0*<var>x</var><sub>22</sub>  </td><td>+   0*<var>x</var><sub>32</sub>  </td><td>+   0*<var>x</var><sub>13</sub>  </td><td>+   0*<var>x</var><sub>23</sub>  </td><td>+   0*<var>x</var><sub>33</sub>  </td><td>= <sup>16</sup>⁄<sub>4</sub></td></tr>
        <tr><td>   –<var>x</var><sub>11</sub>/4</td><td>+ 1.0*<var>x</var><sub>21</sub>  </td><td>+    –<var>x</var><sub>31</sub>/4</td><td>+   0*<var>x</var><sub>12</sub>  </td><td>+    –<var>x</var><sub>22</sub>/4</td><td>+   0*<var>x</var><sub>32</sub>  </td><td>+   0*<var>x</var><sub>13</sub>  </td><td>+   0*<var>x</var><sub>23</sub>  </td><td>+   0*<var>x</var><sub>33</sub>  </td><td>= <sup>20</sup>⁄<sub>4</sub></td></tr>
        <tr><td>  0*<var>x</var><sub>11</sub>  </td><td>+    –<var>x</var><sub>21</sub>/4</td><td>+ 1.0*<var>x</var><sub>31</sub>  </td><td>+   0*<var>x</var><sub>12</sub>  </td><td>+   0*<var>x</var><sub>22</sub>  </td><td>+    –<var>x</var><sub>32</sub>/4</td><td>+   0*<var>x</var><sub>13</sub>  </td><td>+   0*<var>x</var><sub>23</sub>  </td><td>+   0*<var>x</var><sub>33</sub>  </td><td>= <sup>44</sup>⁄<sub>4</sub></td></tr>
        <tr><td>   –<var>x</var><sub>11</sub>/4</td><td>+   0*<var>x</var><sub>21</sub>  </td><td>+   0*<var>x</var><sub>31</sub>  </td><td>+ 1.0*<var>x</var><sub>12</sub>  </td><td>+    –<var>x</var><sub>22</sub>/4</td><td>+   0*<var>x</var><sub>32</sub>  </td><td>+    –<var>x</var><sub>13</sub>/4</td><td>+   0*<var>x</var><sub>23</sub>  </td><td>+   0*<var>x</var><sub>33</sub>  </td><td>=  <sup>8</sup>⁄<sub>4</sub></td></tr>
        <tr><td>  0*<var>x</var><sub>11</sub>  </td><td>+    –<var>x</var><sub>21</sub>/4</td><td>+   0*<var>x</var><sub>31</sub>  </td><td>+    –<var>x</var><sub>12</sub>/4</td><td>+ 1.0*<var>x</var><sub>22</sub>  </td><td>+    –<var>x</var><sub>32</sub>/4</td><td>+   0*<var>x</var><sub>13</sub>  </td><td>+    –<var>x</var><sub>23</sub>/4</td><td>+   0*<var>x</var><sub>33</sub>  </td><td>=  <sup>0</sup>⁄<sub>4</sub></td></tr>
        <tr><td>  0*<var>x</var><sub>11</sub>  </td><td>+   0*<var>x</var><sub>21</sub>  </td><td>+    –<var>x</var><sub>31</sub>/4</td><td>+   0*<var>x</var><sub>12</sub>  </td><td>+    –<var>x</var><sub>22</sub>/4</td><td>+ 1.0*<var>x</var><sub>32</sub>  </td><td>+   0*<var>x</var><sub>13</sub>  </td><td>+   0*<var>x</var><sub>23</sub>  </td><td>+    –<var>x</var><sub>33</sub>/4</td><td>= <sup>28</sup>⁄<sub>4</sub></td></tr>
        <tr><td>  0*<var>x</var><sub>11</sub>  </td><td>+   0*<var>x</var><sub>21</sub>  </td><td>+   0*<var>x</var><sub>31</sub>  </td><td>+    –<var>x</var><sub>12</sub>/4</td><td>+   0*<var>x</var><sub>22</sub>  </td><td>+   0*<var>x</var><sub>32</sub>  </td><td>+ 1.0*<var>x</var><sub>13</sub>  </td><td>+    –<var>x</var><sub>23</sub>/4</td><td>+   0*<var>x</var><sub>33</sub>  </td><td>= <sup>32</sup>⁄<sub>4</sub></td></tr>
        <tr><td>  0*<var>x</var><sub>11</sub>  </td><td>+   0*<var>x</var><sub>21</sub>  </td><td>+   0*<var>x</var><sub>31</sub>  </td><td>+   0*<var>x</var><sub>12</sub>  </td><td>+    –<var>x</var><sub>22</sub>/4</td><td>+   0*<var>x</var><sub>32</sub>  </td><td>+    –<var>x</var><sub>13</sub>/4</td><td>+ 1.0*<var>x</var><sub>23</sub>  </td><td>+    –<var>x</var><sub>33</sub>/4</td><td>= <sup>24</sup>⁄<sub>4</sub></td></tr>
        <tr><td>  0*<var>x</var><sub>11</sub>  </td><td>+   0*<var>x</var><sub>21</sub>  </td><td>+   0*<var>x</var><sub>31</sub>  </td><td>+   0*<var>x</var><sub>12</sub>  </td><td>+   0*<var>x</var><sub>22</sub>  </td><td>+    –<var>x</var><sub>32</sub>/4</td><td>+   0*<var>x</var><sub>13</sub>  </td><td>+    –<var>x</var><sub>23</sub>/4</td><td>+ 1.0*<var>x</var><sub>33</sub>  </td><td>= <sup>56</sup>⁄<sub>4</sub></td></tr>
        </tbody></table>

      <p>If you compare the two sets of equations carefully, you will see that
        they are the same, but the second set is clearly more complicated. You may
        be wondering why go to the fuss. Well, if you remember your matrix
        multiplication, you can express that second set of equations as simply:</p>

      <table style="border-spacing: 0; margin: 0 auto; text-align: center">
        <tbody><tr><td style="border-color: #000; border-style: solid; border-width: 0 0 0 1px"> 1.0 </td><td>-0.25</td><td> 0.0 </td><td>-0.25</td><td> 0.0 </td><td> 0.0 </td><td> 0.0 </td><td> 0.0 </td><td style="border-color: #000; border-style: solid; border-width: 0 1px 0 0"> 0.0 </td><td>       </td><td style="border-color: #000; border-style: solid; border-width: 0 1px"><var>x</var><sub>11</sub></td><td> </td><td style="border-color: #000; border-style: solid; border-width: 0 1px"> 4.0</td></tr>
        <tr><td style="border-color: #000; border-style: solid; border-width: 0 0 0 1px">-0.25</td><td> 1.0 </td><td>-0.25</td><td> 0.0 </td><td>-0.25</td><td> 0.0 </td><td> 0.0 </td><td> 0.0 </td><td style="border-color: #000; border-style: solid; border-width: 0 1px 0 0"> 0.0 </td><td>       </td><td style="border-color: #000; border-style: solid; border-width: 0 1px"><var>x</var><sub>21</sub></td><td> </td><td style="border-color: #000; border-style: solid; border-width: 0 1px"> 5.0</td></tr>
        <tr><td style="border-color: #000; border-style: solid; border-width: 0 0 0 1px"> 0.0 </td><td>-0.25</td><td> 1.0 </td><td> 0.0 </td><td> 0.0 </td><td>-0.25</td><td> 0.0 </td><td> 0.0 </td><td style="border-color: #000; border-style: solid; border-width: 0 1px 0 0"> 0.0 </td><td>       </td><td style="border-color: #000; border-style: solid; border-width: 0 1px"><var>x</var><sub>31</sub></td><td> </td><td style="border-color: #000; border-style: solid; border-width: 0 1px">11.0</td></tr>
        <tr><td style="border-color: #000; border-style: solid; border-width: 0 0 0 1px">-0.25</td><td> 0.0 </td><td> 0.0 </td><td> 1.0 </td><td>-0.25</td><td> 0.0 </td><td>-0.25</td><td> 0.0 </td><td style="border-color: #000; border-style: solid; border-width: 0 1px 0 0"> 0.0 </td><td>       </td><td style="border-color: #000; border-style: solid; border-width: 0 1px"><var>x</var><sub>12</sub></td><td> </td><td style="border-color: #000; border-style: solid; border-width: 0 1px"> 2.0</td></tr>
        <tr><td style="border-color: #000; border-style: solid; border-width: 0 0 0 1px"> 0.0 </td><td>-0.25</td><td> 0.0 </td><td>-0.25</td><td> 1.0 </td><td>-0.25</td><td> 0.0 </td><td>-0.25</td><td style="border-color: #000; border-style: solid; border-width: 0 1px 0 0"> 0.0 </td><td>×</td><td style="border-color: #000; border-style: solid; border-width: 0 1px"><var>x</var><sub>22</sub></td><td>=</td><td style="border-color: #000; border-style: solid; border-width: 0 1px"> 0.0</td></tr>
        <tr><td style="border-color: #000; border-style: solid; border-width: 0 0 0 1px"> 0.0 </td><td> 0.0 </td><td>-0.25</td><td> 0.0 </td><td>-0.25</td><td> 1.0 </td><td> 0.0 </td><td> 0.0 </td><td style="border-color: #000; border-style: solid; border-width: 0 1px 0 0">-0.25</td><td>       </td><td style="border-color: #000; border-style: solid; border-width: 0 1px"><var>x</var><sub>32</sub></td><td> </td><td style="border-color: #000; border-style: solid; border-width: 0 1px"> 7.0</td></tr>
        <tr><td style="border-color: #000; border-style: solid; border-width: 0 0 0 1px"> 0.0 </td><td> 0.0 </td><td> 0.0 </td><td>-0.25</td><td> 0.0 </td><td> 0.0 </td><td> 1.0 </td><td>-0.25</td><td style="border-color: #000; border-style: solid; border-width: 0 1px 0 0"> 0.0 </td><td>       </td><td style="border-color: #000; border-style: solid; border-width: 0 1px"><var>x</var><sub>13</sub></td><td> </td><td style="border-color: #000; border-style: solid; border-width: 0 1px"> 8.0</td></tr>
        <tr><td style="border-color: #000; border-style: solid; border-width: 0 0 0 1px"> 0.0 </td><td> 0.0 </td><td> 0.0 </td><td> 0.0 </td><td>-0.25</td><td> 0.0 </td><td>-0.25</td><td> 1.0 </td><td style="border-color: #000; border-style: solid; border-width: 0 1px 0 0">-0.25</td><td>       </td><td style="border-color: #000; border-style: solid; border-width: 0 1px"><var>x</var><sub>23</sub></td><td> </td><td style="border-color: #000; border-style: solid; border-width: 0 1px"> 6.0</td></tr>
        <tr><td style="border-color: #000; border-style: solid; border-width: 0 0 0 1px"> 0.0 </td><td> 0.0 </td><td> 0.0 </td><td> 0.0 </td><td> 0.0 </td><td>-0.25</td><td> 0.0 </td><td>-0.25</td><td style="border-color: #000; border-style: solid; border-width: 0 1px 0 0"> 1.0 </td><td>       </td><td style="border-color: #000; border-style: solid; border-width: 0 1px"><var>x</var><sub>33</sub></td><td> </td><td style="border-color: #000; border-style: solid; border-width: 0 1px">14.0</td></tr>
        </tbody></table>

      <p>The left-hand matrix consists only of the values needed to pick out the
        appropriate values of the coefficients. Thus that left-hand matrix will be
        always the same for any plate. It is the right-hand side that contains the
        constant values of the outer edge of the plate. Your program must compute
        that vector from the data values entered by the user.</p>

      <p>In order to solve this system of equations, your program should
        initialize a matrix A and a vector B to values as just described. Two
        formats for commands to initialize A are shown below:</p>

      <table style="margin: 0 auto; text-align: center; width: 75%">
        <tbody><tr>
          <td>
<pre v-highlightjs><code class="plain">A(1,:) = [ values of first row ];
A(2,:) = [ values of second row ];
   .
   .
   .
A(9,:) = [ values of ninth row ];</code></pre>
          </td>
          <td style="margin: 0 auto">
<pre v-highlightjs><code class="plain">A = [ values of first row; ...
      values of second row; ...
   .
   .
   .
   values of ninth row ];</code></pre>
          </td>
        </tr>
        </tbody></table>

      <p>The solution is then the result of the Matlab expression <code>A\B</code>.</p>
    </article>

    <article>
      <h2>Optional (for fun only)</h2>

      <p>One really neat visualization you can do is to animate the difference
        between the grid at each iteration of the iterative solution and the
        analytical solution. You should choose a colormap that has red for negative
        values, white in the middle for values around zero and blue for positive
        values. You can make a movie called frames with code that looks like this
        (assuming iterations is your variable for the # of iterations, and
        threshold is your threshold value):</p>

      <pre v-highlightjs><code class="plain">redwhitebluecmap=[1.00 0.00 0.00;
                  1.00 0.25 0.25;
                  1.00 0.50 0.50;
                  1.00 0.75 0.75;
                  1.00 1.00 1.00;
                  1.00 1.00 1.00;
                  0.75 0.75 1.00;
                  0.50 0.50 1.00;
                  0.25 0.25 1.00;
                  0.00 0.00 1.00];
for i = 1:iterations+1  %% 3 iterations yields frame 1(initial),2,3,4
    imagesc( %%%YOUR-DIFFERENCE-GRID-HERE%%% ); %% Iteration-Analytical

    colormap(redwhitebluecmap);        %% Set the colormap
    axis square off equal;             %% Set the axes
    caxis([-5*threshold,5*threshold]); %% Set colormap map (10 steps)
    colorbar;%% Show a colorbar, add a title
    title(sprintf('Temp grid convergence (iter-ans). Threshold %f, Iteration %d', threshold, i-1));
    frames(i) = getframe;              %% Save the frame
end</code></pre>

      <p>You can then play your movie with the movie command (while it’s animating, feel free
        to stand and sing the national anthem if the colormap choice makes you feel overly
        patriotic):</p>
      <pre v-highlightjs><code class="plain">&gt;&gt; movie(frames)</code></pre>
    </article>

    <article>
      <h2>Miscellaneous Requirements</h2>

      <p>For full credit, you must divide this problem carefully into subproblems
        and represent them as subprograms. These will include a subroutine to fill
        the 9×9 coefficient matrix as shown above, and the 9-element
        right-hand-side vector based on the temperature values at the external
        points of the plate. You should also have a subroutine to compute the new
        temperatures at each step, and a logical function to determine if
        equilibrium has been reached. You won’t be able to store the new
        temperatures into the plate array immediately, since the averages for state
        <var>t</var> must be computed from the temperatures at state
        <var>t</var>–1.</p>

      <p>In no case should you have a function that contains more than
        twenty-four lines of Matlab.</p>

      <p>In this assignment you should assume temperatures measured in degrees
        Kelvin.  Thus, negative temperatures are impossible (by the definition of
        the Kelvin scale and absolute zero). Do something reasonable with illegal
        data values.</p>

      <p>As with earlier problems, test the subprograms separately, using data
        for which you can easily compute the correct answers by hand. Print out
        copies of your programs and test "drivers" so that tutors can verify that
        your individual routines work. Make sure you can explain any difference
        between the solution values you produce from the two parts. If you make the
        difference criterion for Part 1 a variable, you should be able to show that
        the differences between Part 1 and Part 2 decrease for smaller differences
        between the averages.</p>

      <p>You should strive to make your code as general as possible. As an
        example, you should not hardcode the 5×5 nature of the problem in
        your iterative solution. You may, however, hardcode it in your
        <var>A</var><var>x</var> = <var>b</var> analytical solution.</p>

      <p>Use format statements to specify that no more than two decimal digits
        are to be printed for each value.</p>
    </article>

    <article>
      <h2>Checklist</h2>
      <p>A satisfactory grade on this program requires</p>
      <ul>
        <li>correct results for simulation and for solution of systems of equations;</li>
        <li>programs split into subprograms as specified:
          <ul>
            <li>subroutine to fill the coefficient matrix;</li>
            <li>subroutine to update the simulation array;</li>
            <li>logical function to determine equilibrium;</li>
          </ul>
        </li>
        <li>subprograms as general as possible (e.g., no hardcoding of the 5×5 array sizes</li>
        <li>except in the <var>A</var><var>x</var> = <var>b</var> analytical solution);</li>
        <li>evidence of individual testing of subprograms;</li>
        <li>no routine longer than twenty-four lines of code;</li>
        <li>variable and subprogram names that reflect their use;</li>
        <li>systematic indentation of test and loop bodies;</li>
        <li>comments at the head of the main program and subprograms that explain the purpose of each;</li>
        <li>declaration of all variables in a "dictionary";</li>
        <li>negative temperatures detected and dealt with appropriately;</li>
        <li>clean case analysis and simple loop structuring;</li>
        <li>correct answers;</li>
        <li>input of plate temperature values from a file;</li>
        <li>nicely formatted output;</li>
        <li>explanation of differences between approximate and analytic solutions.</li>
      </ul>
    </article>
  </section>
</template>



// WEBPACK FOOTER //
// src/components/assignments/matlab/HeatTransfer.vue